import React, { useEffect, useRef, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
// Individual extensions rather than StarterKit: it also bundles link, code block,
// blockquote, strike and underline, none of which this toolbar exposes.
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import BoldMark from "@tiptap/extension-bold";
import ItalicMark from "@tiptap/extension-italic";
import Heading from "@tiptap/extension-heading";
import HardBreak from "@tiptap/extension-hard-break";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { BulletList, OrderedList, ListItem, ListKeymap, TaskList, TaskItem } from "@tiptap/extension-list";
import { Dropcursor, Gapcursor, Placeholder, UndoRedo } from "@tiptap/extensions";
import { Markdown } from "tiptap-markdown";
import { api } from "../lib/api";
import { NotebookPen, X, Bold, Italic, Heading2, List, ListOrdered, ListChecks, Minus } from "lucide-react";

// Notes are stored as markdown so the study card and existing notes keep working.
// The editor is the only place that speaks markdown; the user never sees it.
//
// TipTap writes task items as loose list items (a blank line between each). The
// app's own renderer treats a blank line as the end of a list, so tighten runs of
// list items back up before saving. Only collapses a gap where BOTH sides are list
// items — the blank line between a paragraph and a list is meaningful and stays.
const LI = String.raw`[ \t]*(?:- \[[ xX]\]|[-*]|\d+\.)[ \t]`;
const TIDY = new RegExp(`(${LI}[^\\n]*)\\n\\n(?=${LI})`, "g");
const toMarkdown = (editor) => editor.storage.markdown.getMarkdown().replace(TIDY, "$1\n").trim();

const TOOLS = [
  { id: "bold", icon: Bold, title: "Bold (Ctrl+B)", is: "bold", run: (c) => c.toggleBold() },
  { id: "italic", icon: Italic, title: "Italic (Ctrl+I)", is: "italic", run: (c) => c.toggleItalic() },
  { id: "h2", icon: Heading2, title: "Heading", is: "heading", attrs: { level: 2 }, run: (c) => c.toggleHeading({ level: 2 }) },
  { id: "bullet", icon: List, title: "Bullet list", is: "bulletList", run: (c) => c.toggleBulletList() },
  { id: "number", icon: ListOrdered, title: "Numbered list", is: "orderedList", run: (c) => c.toggleOrderedList() },
  { id: "check", icon: ListChecks, title: "Checklist", is: "taskList", run: (c) => c.toggleTaskList() },
  { id: "divider", icon: Minus, title: "Divider", run: (c) => c.setHorizontalRule() },
];

export default function NotesPanel({ sessionId, initialNotes, open, onClose }) {
  const [saved, setSaved] = React.useState(true);
  const timer = useRef(null);
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;

  const save = useCallback((md) => {
    setSaved(false);
    clearTimeout(timer.current);
    const sid = sessionRef.current;
    timer.current = setTimeout(async () => {
      try {
        await api.patchNotes(sid, md);
        setSaved(true);
      } catch (e) {
        setSaved(false); // leave it showing "Saving…" rather than claiming a save that failed
      }
    }, 700);
  }, []);

  const editor = useEditor({
    extensions: [
      Document, Paragraph, Text, BoldMark, ItalicMark, HardBreak, HorizontalRule,
      Heading.configure({ levels: [1, 2, 3] }),
      BulletList, OrderedList, ListItem, ListKeymap,
      Dropcursor, Gapcursor, UndoRedo,
      TaskList,
      TaskItem.configure({ nested: false }),
      Placeholder.configure({ placeholder: "Jot down what clicks. Use the toolbar for headings, bold, bullets and checklists. Auto-saved." }),
      Markdown.configure({ html: false, transformPastedText: true, bulletListMarker: "-" }),
    ],
    content: initialNotes || "",
    editorProps: { attributes: { class: "px-4 py-4 text-sm leading-relaxed text-ink" } },
    onUpdate: ({ editor: ed }) => save(toMarkdown(ed)),
  });

  // Reload only when the session changes. Reacting to initialNotes as well would let
  // a background session refetch overwrite what is being typed, since the debounced
  // save has not landed yet.
  useEffect(() => {
    // emitUpdate:false — loading a session must not look like an edit, or opening
    // a note would immediately re-save a re-serialized copy of it.
    if (editor && !editor.isDestroyed) editor.commands.setContent(initialNotes || "", { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, editor]);

  // Flush a pending save rather than dropping it when the panel goes away.
  useEffect(() => () => {
    if (!timer.current) return;
    clearTimeout(timer.current);
    if (editor && !editor.isDestroyed) api.patchNotes(sessionRef.current, toMarkdown(editor)).catch(() => {});
  }, [editor]);

  const apply = (tool) => {
    if (!editor) return;
    tool.run(editor.chain().focus()).run();
  };

  return (
    <>
      {open && <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden" onClick={onClose} />}
      <aside
        data-testid="notes-panel"
        className={`fixed right-0 top-0 z-40 flex h-full w-[85vw] max-w-sm flex-col border-l border-hair bg-surface transition-transform duration-300 lg:static lg:z-0 lg:w-80 lg:translate-x-0 ${open ? "translate-x-0" : "translate-x-full lg:translate-x-full lg:hidden"}`}
      >
        <div className="flex items-center justify-between border-b border-hair px-4 py-3">
          <div className="flex items-center gap-2">
            <NotebookPen className="h-4 w-4 text-muted" />
            <span className="font-display text-lg">Notes</span>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-muted hover:bg-white/5 hover:text-ink lg:hidden">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-0.5 border-b border-hair px-2 py-1.5" data-testid="notes-toolbar">
          {TOOLS.map((t, i) => {
            const active = editor && t.is && editor.isActive(t.is, t.attrs);
            return (
              <React.Fragment key={t.id}>
                {i === 2 || i === 6 ? <span className="mx-1 h-4 w-px bg-white/10" /> : null}
                <button
                  data-testid={`notes-tool-${t.id}`}
                  onClick={() => apply(t)}
                  title={t.title}
                  aria-pressed={!!active}
                  className={`press rounded-md p-1.5 transition-colors hover:bg-white/10 hover:text-ink ${active ? "bg-white/10 text-ink" : "text-muted"}`}
                >
                  <t.icon className="h-4 w-4" />
                </button>
              </React.Fragment>
            );
          })}
        </div>

        <div className="notes-editor min-h-0 flex-1 overflow-y-auto" data-testid="notes-editor">
          <EditorContent editor={editor} />
        </div>

        <div className="flex items-center justify-between border-t border-hair px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-muted">
          <span>{saved ? "Saved" : "Saving…"}</span>
        </div>
      </aside>
    </>
  );
}
